import axios from "axios";
import { baseURL } from "../Constants";

const url = `${baseURL}/Wrapped`;

// ---- Season Wrapped (tasks/wrapped-design.md) ----

export interface WrappedTotals {
    games: number;
    matches: number;
    rounds: number;
    kills: number;
    deaths: number;
    assists: number;
    adr: number;
    kast: number;
    rating: number;
    estimatedHours: number;
}

export interface WrappedGamePoint {
    gameId: string;
    matchId: string;
    matchTime?: string;
    mapName?: string;
    opponentTeamId?: string;
    opponentTeamName?: string;
    rating: number;
    won: boolean;
}

export interface WrappedBestGame {
    gameId: string;
    matchId: string;
    matchTime?: string;
    mapName?: string;
    opponentTeamId?: string;
    opponentTeamName?: string;
    rating: number;
    kills: number;
    deaths: number;
    assists: number;
    adr: number;
    won: boolean;
}

export interface WrappedMapStat {
    mapName: string;
    games: number;
    rating: number;
    winRate: number;
}

export interface WrappedRoles {
    distribution: Record<string, number>;
    dominantCategory?: string;
    avgAlonePct?: number;
    roundsTracked: number;
}

export interface WrappedHomeArea {
    mapName: string;
    areaName: string;
    totalSeconds: number;
}

export interface WrappedBiggestClutch {
    enemyCount: number;
    gameId: string;
    matchId: string;
    mapName?: string;
    opponentTeamName?: string;
    matchTime?: string;
}

export interface WrappedAreaSlice {
    areaName: string;
    seconds: number;
}

export interface WrappedAreaDensity {
    mapName: string;
    totalSeconds: number;
    areas: WrappedAreaSlice[];
}

export interface WrappedPositionCell {
    layer: number;
    /** Sub-type: weapon id for kill/death/victim layers, replay packet type for utility. */
    kind: number;
    x: number;
    y: number;
    count: number;
}

export interface WrappedPositionMapData {
    mapName: string;
    total: number;
    cells: WrappedPositionCell[];
}

export interface WrappedPositions {
    /** World units per cell — the renderer needs it to place cell footprints. */
    cellSize: number;
    /** Every map with data, busiest first. */
    maps: WrappedPositionMapData[];
}

export interface WrappedClutches {
    attempts1v1: number;
    wins1v1: number;
    attempts1v2: number;
    wins1v2: number;
    attempts1v3: number;
    wins1v3: number;
    attempts1v4: number;
    wins1v4: number;
    attempts1v5: number;
    wins1v5: number;
    totalWins: number;
    totalAttempts: number;
    biggest?: WrappedBiggestClutch;
}

export interface WrappedDuels {
    openingAttempts: number;
    openingWins: number;
    firstKills: number;
    firstDeaths: number;
}

export interface WrappedMultikills {
    twoK: number;
    threeK: number;
    fourK: number;
    aces: number;
    aceGame?: WrappedBiggestClutch;
}

export interface WrappedOpponentStat {
    steamId: string;
    playerName?: string;
    killsOnYou: number;
    yourKillsOnThem: number;
}

export interface WrappedFlash {
    timeFlashedSeconds: number;
    enemyTimeFlashedSeconds: number;
    enemiesFlashed: number;
    utilityDamage: number;
}

export interface WrappedWeapons {
    awpKills: number;
    awpShare: number;
    pistolKills: number;
    pistolRoundsWon: number;
    hsPct: number;
}

export interface WrappedHalfStat {
    games: number;
    rating: number;
    winRate: number;
}

export interface WrappedHalves {
    breakStart: string;
    firstHalf: WrappedHalfStat;
    secondHalf: WrappedHalfStat;
}

export interface WrappedTransfer {
    fromTeamName?: string;
    toTeamName?: string;
    transferDate: string;
}

export interface WrappedPercentile {
    stat: string;
    value: number;
    leaguePercentile: number;
    divisionPercentile?: number;
    /** Pool mean — draw "par" from this, never from a hard-coded 1.00 (Rating 3.0 runs above it). */
    leagueMean: number;
    divisionMean?: number;
}

export interface WrappedRank {
    leagueRank: number;
    leaguePool: number;
    divisionRank?: number;
    divisionPool?: number;
    seasonHigh: number;
}

export interface WrappedAttendance {
    yourGames: number;
    teamGames: number;
}

export interface WrappedClip {
    id: number;
    clipUrl: string;
    thumbnailUrl?: string;
    gameId: string;
    roundNr: number;
    killCount: number;
    mapName?: string;
}

export interface WrappedFaceit {
    elo?: number;
    nickname?: string;
}

export interface WrappedEloPoint {
    date: string;
    elo: number;
}

export interface WrappedElo {
    start: number;
    end: number;
    net: number;
    gained: number;
    lost: number;
    high: number;
    low: number;
    points: WrappedEloPoint[];
}

export interface WrappedResponse {
    eligible: boolean;
    steamId: string;
    playerName?: string;
    season: number;
    teamId?: string;
    teamName?: string;
    teamLogo?: string;
    division?: string;
    totals: WrappedTotals;
    ratingJourney: WrappedGamePoint[];
    bestGame?: WrappedBestGame;
    bestGames: WrappedBestGame[];
    areaDensity?: WrappedAreaDensity;
    positions?: WrappedPositions;
    rank?: WrappedRank;
    longestWinStreak: number;
    longestLossStreak: number;
    maps: WrappedMapStat[];
    roles?: WrappedRoles;
    homeArea?: WrappedHomeArea;
    clutches: WrappedClutches;
    duels: WrappedDuels;
    multikills: WrappedMultikills;
    nemesis: WrappedOpponentStat[];
    victims: WrappedOpponentStat[];
    flash: WrappedFlash;
    weapons: WrappedWeapons;
    halves?: WrappedHalves;
    transfer?: WrappedTransfer;
    percentiles: WrappedPercentile[];
    attendance?: WrappedAttendance;
    bestClip?: WrappedClip;
    bestClips?: WrappedClip[];
    archetype: string;
    faceit?: WrappedFaceit;
    elo?: WrappedElo;
    dataPack?: WrappedDataPack;
}

export interface WrappedTeammateStat {
    steamId: string;
    playerName?: string;
    count: number;
}

/**
 * Trade pairing, teammate flashes, team kills and style kills. Only present for seasons Wrapped
 * is offered for (all fully data-pack-parsed); absent otherwise, so slides reading it stay
 * conditional on the section existing.
 */
export interface WrappedDataPack {
    trades: number;
    tradesReceived: number;
    teamKills: number;
    teammatesFlashed: number;
    teammateFlashDuration: number;
    wallbangKills: number;
    noscopeKills: number;
    smokeKills: number;
    blindKills: number;
    bodyguards: WrappedTeammateStat[];
    saved: WrappedTeammateStat[];
    blindedBy: WrappedTeammateStat[];
    blinded: WrappedTeammateStat[];
}

export const getWrapped = async (
    steamId: string,
    season: number
): Promise<WrappedResponse | null> => {
    try {
        const response = await axios.get(`${url}/${steamId}/${season}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching wrapped:", error);
        return null;
    }
};

/** CORS-safe player photo (proxied through our API) for the shareable PNG card. */
export const wrappedPhotoUrl = (steamId: string, width = 200): string =>
    `${url}/photo/${steamId}?width=${width}`;

// ---- View / completion tracking (server-side, so the nudge follows the account) ----

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });

export type WrappedTrackEvent = "view" | "complete" | "dismiss";

export interface WrappedStatus {
    viewed: boolean;
    completed: boolean;
    dismissed: boolean;
    eligible: boolean;
}

/** Record that the caller viewed/completed/dismissed their OWN wrapped (fire-and-forget). */
export const trackWrapped = async (season: number, event: WrappedTrackEvent): Promise<void> => {
    try {
        await axios.post(`${url}/track/${season}`, { event }, { headers: authHeader() });
    } catch {
        // best-effort — tracking must never break the page
    }
};

/** The caller's own wrapped status (viewed/completed/dismissed) — drives the nudge across devices. */
export const getWrappedStatus = async (season: number): Promise<WrappedStatus | null> => {
    try {
        const response = await axios.get(`${url}/me/${season}/status`, { headers: authHeader() });
        return response.data;
    } catch {
        return null;
    }
};

/** Whether a given player has an eligible wrapped that season (public) — gates the profile banner. */
export const getWrappedEligible = async (steamId: string, season: number): Promise<boolean> => {
    try {
        const response = await axios.get(`${url}/${steamId}/${season}/eligible`);
        return !!response.data?.eligible;
    } catch {
        return false;
    }
};
